<template>
  <div class="newsletter-subscribe-form">
    <!-- Submit success -->
    <div v-if="submitSuccess" class="newsletter-subscribe-form__messages">
      <DefaultIconsCheck class="newsletter-subscribe-form__messages-icon" />
      <h3 class="h3" v-html="$t('newsletter.subscribe_form_submit_success_title')" />
      <p class="newsletter-subscribe-form__messages-subtitle" v-html="$t('newsletter.subscribe_form_submit_success_subtitle')" />
      <UButton class="btn newsletter-subscribe-form__btn-close" @click="onClose">
        {{ $t('newsletter.subscribe_form_done') }}
      </UButton>
    </div>
    <!-- Form -->
    <UForm v-else :validate="validate" :state="state" class="space-y-4" @submit="onSubmit">

      <div>
        <!-- Modal title -->
        <h1 class="h3">{{ $t('newsletter.subscribe_form_title') }}</h1>
        <!-- Modal subtitle -->
        <span class="newsletter-subscribe-form__subtitle">
          {{ $t('newsletter.subscribe_form_subtitle') }}
        </span>
      </div>

      <UFormGroup :label="$t('newsletter.subscribe_form_email')" name="email" required :ui="{label:{wrapper: 'input'}}">
        <UInput v-model="state.email" :disabled="newslettersStore.isLoading " placeholder="@" />
      </UFormGroup>

      <div
        v-if="submitError"
        class="newsletter-subscribe-form__messages newsletter-subscribe-form__messages--error"
      >
        <h3 class="h4" v-html="$t('newsletter.subscribe_form_submit_error_title')" />
        <p class="newsletter-subscribe-form__messages-subtitle" v-html="$t('newsletter.subscribe_form_submit_error_subtitle')" />
      </div>

      <UButton class="btn btn--lg" type="submit" :disabled="newslettersStore.isLoading" :loading="newslettersStore.isLoading">
        {{ $t('newsletter.subscribe_form_submit') }}
      </UButton>

      <UFormGroup name="terms" required>
        <UCheckbox
          v-model="state.consents.terms"
          name="terms"
          :disabled="newslettersStore.isLoading "
        >
          <template #label>
            <span class="input" v-html="newsletter.contactListConsentText" />
            <span class="required">*</span>
          </template>
        </UCheckbox>

      </UFormGroup>

      <UFormGroup v-if="newsletter.secondaryContactListId" name="secondaryTerms">
        <UCheckbox
          v-model="state.consents.secondaryTerms"
          input-class="input"
          :disabled="newslettersStore.isLoading "
        >
          <template #label>
            <span class="input" v-html="newsletter.secondaryContactListConsentText" />
          </template>
        </UCheckbox>
      </UFormGroup>

    </UForm>
  </div>
</template>

<script setup lang="ts">
import type {PropType} from "vue"
import type NewsletterLocal from "~/libs/interfaces/NewsletterLocal"
import type {FormError, FormSubmitEvent} from "#ui/types"
import {useI18n} from "vue-i18n"

const props = defineProps({
  newsletter: {type: Object as PropType<NewsletterLocal>, required: true},
})

const {t} = useI18n()
const { executeRecaptcha  } = useGoogleRecaptcha()
const newslettersStore = useNewsletterStore()

const submitSuccess = ref(false)
const submitError = ref(false)
const isSubmitDisabled = computed(() => {
  return !state.consents.terms || !state.email
  // return !state.consents.terms || !state.consents.newslettter
})

const state = reactive({
  email: undefined,
  consents: {
    terms: undefined,
    secondaryTerms: undefined,
  },
})

const emits = defineEmits(['close'])

const onClose = () => {
  emits('close')
}

const validate = (state: any): FormError[] => {
  const validRegex = new RegExp("^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$", "i")
  const errors = []

  if (!state.email) {
    errors.push({ path: 'email', message: t('forms.required') })
  }
  if (state.email && !state.email.match(validRegex)) {
    errors.push({ path: 'email', message: t('forms.invalid_email') })
  }

  if (!state.consents.terms) {
    errors.push({ path: 'terms', message: t('forms.required') })
  }
  // if (!state.consents.secondaryTerms) {
  //   errors.push({ path: 'secondaryTerms', message: t('forms.required') })
  // }

  return errors
}

const onSubmit = async (event: FormSubmitEvent<any>) => {
  submitSuccess.value = false
  submitError.value = false

  // recaptcha
  const { token } = await executeRecaptcha('submit')

  const contactListIds = [props.newsletter?.contactListId]
  if (state.consents.secondaryTerms && props.newsletter?.secondaryContactListId) {
    contactListIds.push(props.newsletter?.secondaryContactListId)
  }

  const result = await newslettersStore.addContact(state.email, contactListIds, token)

  if (result?.status !== 'created') {
    submitError.value = true
  } else {
    submitSuccess.value = true
  }
}

</script>

<style lang="scss" scoped>

</style>
